// relatedItemsTable.tsx : Un composant pour afficher une table d'éléments liés avec des actions possibles sur chaque élément.
// Il est utilisé par le RelatedItemsManager, mais peut aussi être utilisé seul si on veut juste afficher une table d'éléments liés sans gestion de la liaison/déliaison.

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Table } from '@radix-ui/themes';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import * as Tooltip from '@radix-ui/react-tooltip';

function formatValue(value: unknown) {
	if (value == null || value === undefined || value === '') return '';

	// Date
	if (value instanceof Date) {
		return value.toLocaleDateString('fr-FR');
	}

	// Chaîne de caractères au format date ???
	if (typeof value === 'string' && !isNaN(Date.parse(value))) {
		return new Date(value).toLocaleDateString('fr-FR');
	}

	// Objet avec referenceNumber
	if (typeof value === 'object' && 'referenceNumber' in value) {
		const ref = (value as { referenceNumber?: unknown }).referenceNumber;
		if (typeof ref === 'string' || typeof ref === 'number') {
			return String(ref);
		}
	}

	// Tableau de badgeMovements
	if (Array.isArray(value)) {
		const names: string[] = value
			.map((v) => {
				if (typeof v !== 'object' || v === null) return null;

				const movement = v as {
					ownerFirstName?: unknown;
					ownerLastName?: unknown;
				};

				if (
					typeof movement.ownerFirstName === 'string' &&
					typeof movement.ownerLastName === 'string'
				) {
					return `${movement.ownerFirstName} ${movement.ownerLastName}`;
				}

				if (typeof movement.ownerLastName === 'string') {
					return movement.ownerLastName;
				}

				return null;
			})
			.filter((name): name is string => Boolean(name));

		// Suppression des doublons
		const uniqueNames: string[] = [];
		for (let i = 0; i < names.length; i++) {
			if (uniqueNames.indexOf(names[i]) === -1) {
				uniqueNames.push(names[i]);
			}
		}

		if (uniqueNames.length > 0) {
			return uniqueNames.join(', ');
		}
	}

	// Objet avec label
	if (typeof value === 'object' && 'label' in value) {
		const label = (value as { label?: unknown }).label;
		if (typeof label === 'string' || typeof label === 'number') {
			return String(label);
		}
	}

	// Par défaut, on convertit en string
	return String(value);
}

export interface Column<T> {
	key: keyof T;
	label: string;
	render?: (value: unknown, item: T) => React.ReactNode;
}

/**
 * Représente une cellule de la ligne de totaux en pied de tableau.
 * `columnKey` doit correspondre à la clé d'une colonne existante (ou à 'action' pour la colonne Actions).
 * `value` est la valeur à afficher ; si null/undefined, la cellule sera vide.
 */
export interface FooterTotal<T> {
	columnKey: keyof T | 'action';
	value: React.ReactNode;
}

interface RelatedItemsTableProps<T> {
	title: string;
	items: T[];
	columns: Column<T>[];
	actionIcon: IconDefinition;
	actionColorClass: string;
	onAction: (item: T) => void;
	/** Fonction optionnelle permettant de désactiver le bouton d'action ligne par ligne */
	actionDisabled?: (item: T) => boolean;
	/** Fonction optionnelle fournissant un tooltip par ligne sur le bouton d'action */
	actionTooltip?: (item: T) => string;
	headerActionIcon?: IconDefinition;
	onHeaderAction?: () => void;
	emptyLabel?: string;
	collapsible?: boolean;
	collapsed?: boolean;
	showCount?: boolean;
	readOnly?: boolean;
	readOnlyTooltip?: string;
	idUrlPrefix?: string;
	/** Ligne de totaux affichée en pied de tableau. Par défaut absent (non affiché). */
	footerTotals?: FooterTotal<T>[];
}

export function RelatedItemsTable<T extends { id: number }>({
	title,
	items,
	columns,
	actionIcon,
	actionColorClass,
	onAction,
	actionDisabled,
	actionTooltip,
	headerActionIcon,
	onHeaderAction,
	emptyLabel = 'Aucun élément.',
	collapsible = true,
	collapsed = false,
	showCount = false,
	readOnly = false,
	readOnlyTooltip,
	idUrlPrefix,
	footerTotals,
}: RelatedItemsTableProps<T>) {
	const [expanded, setExpanded] = React.useState(!collapsible || !collapsed);
	return (
		<div className="relative mt-4">
			<div
				className={`flex justify-between items-center mb-2 ${collapsible ? 'cursor-pointer' : ''}`}
				onClick={collapsible ? () => setExpanded(!expanded) : undefined}
			>
				<h3
					className={`${expanded ? 'text-gray-700 font-semibold' : 'text-gray-500'} flex items-center`}
				>
					{collapsible && (
						<FontAwesomeIcon
							icon={faChevronRight}
							className={`w-3 h-3 mr-2 transform transition-transform ${expanded ? 'rotate-90' : ''}`}
						/>
					)}
					{title} {showCount && `(${items.length})`}
				</h3>

				{headerActionIcon && onHeaderAction && (
					<FontAwesomeIcon
						icon={headerActionIcon}
						className="w-4 h-4 text-gray-300 hover:text-green-500 cursor-pointer"
						onClick={(e) => {
							e.stopPropagation(); // évite d’ouvrir/fermer la table
							onHeaderAction();
						}}
					/>
				)}
			</div>

			{expanded && (
				<>
					{!items?.length ? (
						<p className="text-gray-500">{emptyLabel}</p>
					) : (
						<Table.Root variant="ghost" layout="auto">
							<Table.Header>
								<Table.Row>
									{columns.map((col) => (
										<Table.ColumnHeaderCell
											key={col.key as string}
											className="text-gray-500 !font-normal !px-2"
										>
											{col.label}
										</Table.ColumnHeaderCell>
									))}
									<Table.ColumnHeaderCell className="text-right text-gray-500 !font-normal !px-2">
										Actions
									</Table.ColumnHeaderCell>
								</Table.Row>
							</Table.Header>

							<Table.Body>
								{items.map((item) => (
									<Table.Row key={item.id}>
										{columns.map((col, index) => (
											<Table.Cell key={col.key as string} className="!px-2">
												{index === 0 && idUrlPrefix ? (
													<a
														href={`${idUrlPrefix}/${item[col.key]}/edit`}
														className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
													>
														{col.render
															? col.render(item[col.key], item)
															: formatValue(item[col.key])}
													</a>
												) : col.render ? (
													col.render(item[col.key], item)
												) : (
													formatValue(item[col.key])
												)}
											</Table.Cell>
										))}
										<Table.Cell className="text-right !px-2">
											{!readOnly &&
												(() => {
													const isDisabled = actionDisabled?.(item) ?? false;
													const tooltip = actionTooltip?.(item);
													const iconEl = (
														<FontAwesomeIcon
															icon={actionIcon}
															className={`ml-2 w-4 h-4 ${
																isDisabled
																	? 'text-gray-200 cursor-not-allowed'
																	: `text-gray-300 cursor-pointer hover:${actionColorClass}`
															}`}
															onClick={
																isDisabled ? undefined : () => onAction(item)
															}
														/>
													);
													if (tooltip) {
														return (
															<Tooltip.Provider>
																<Tooltip.Root>
																	<Tooltip.Trigger asChild>
																		<span>{iconEl}</span>
																	</Tooltip.Trigger>
																	<Tooltip.Content
																		side="top"
																		align="center"
																		className="max-w-md bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-normal text-left"
																	>
																		{tooltip}
																		<Tooltip.Arrow className="fill-current text-gray-800" />
																	</Tooltip.Content>
																</Tooltip.Root>
															</Tooltip.Provider>
														);
													}
													return iconEl;
												})()}
											{readOnly && !readOnlyTooltip && (
												<FontAwesomeIcon
													icon={faBan}
													className={`ml-2 w-4 h-4 text-gray-300`}
												/>
											)}
											{readOnly && readOnlyTooltip && (
												<Tooltip.Provider>
													<Tooltip.Root>
														<Tooltip.Trigger asChild>
															<FontAwesomeIcon
																icon={faBan}
																className="ml-2 w-4 h-4 text-gray-300"
															/>
														</Tooltip.Trigger>
														<Tooltip.Content
															side="top"
															align="center"
															className="max-w-md bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-normal text-left"
														>
															{readOnlyTooltip}
															<Tooltip.Arrow className="fill-current text-gray-800" />
														</Tooltip.Content>
													</Tooltip.Root>
												</Tooltip.Provider>
											)}
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>

							{/* Ligne de totaux en pied de tableau (optionnelle) */}
							{footerTotals && footerTotals.length > 0 && (
								<tfoot>
									<tr className="border-t border-gray-200">
										{columns.map((col) => {
											const total = footerTotals.find(
												(ft) => ft.columnKey === col.key
											);
											return (
												<td
													key={col.key as string}
													className="text-gray-600 font-semibold text-sm py-2 px-2"
												>
													{total?.value ?? ''}
												</td>
											);
										})}
										{/* Cellule de la colonne Actions — peut porter un total ou rester vide */}
										<td className="text-right text-gray-700 font-semibold text-sm py-2 px-3">
											{footerTotals.find((ft) => ft.columnKey === 'action')
												?.value ?? ''}
										</td>
									</tr>
								</tfoot>
							)}
						</Table.Root>
					)}
				</>
			)}
		</div>
	);
}
export default RelatedItemsTable;
